import type { MeSettings, Occurrence, PaymentKind } from '@klopotun/contracts';
import { formatCivilDate, toCivilDate, type CivilDate } from '@klopotun/domain';
import {
  formatBaseAmount,
  formatOccurrenceAmount,
  occurrenceMarkOf,
  OccurrenceRow,
  type OccurrenceMark,
} from '../../../features/occurrence-view/index.js';
import { formatStoredDateForPeople } from '../../../features/payment-view/index.js';
import { EmptyState, ErrorState, List, Spinner } from '../../../shared/ui/index.js';
import styles from './period-history.module.css';

type PeriodHistoryProps = {
  occurrences: Occurrence[] | undefined;
  kind: PaymentKind;
  isFromBank: boolean;
  settings: MeSettings;
  bankMatchedIds: Set<string>;
  isPending: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  onSelect: (occurrence: Occurrence) => void;
};

const byPeriodStartDescending = (left: Occurrence, right: Occurrence): number => {
  if (left.periodStart === right.periodStart) {
    return 0;
  }

  return left.periodStart < right.periodStart ? 1 : -1;
};

/**
 * Only what has happened: a payment whose date has come, or one confirmed ahead of it. Rows for
 * the months ahead would repeat what the schedule above already says, and the calendar has them.
 */
const hasArrived = (occurrence: Occurrence, today: string): boolean =>
  occurrence.status === 'paid' || occurrence.dueDate <= today;

const BANK_MATCH_MARK: OccurrenceMark = { label: 'з банку', tone: 'muted' };

type PeriodMarkInput = {
  occurrence: Occurrence;
  kind: PaymentKind;
  isFromBank: boolean;
  today: CivilDate;
  settings: MeSettings;
  bankMatchedIds: Set<string>;
};

const periodMarkOf = (input: PeriodMarkInput): OccurrenceMark => {
  const isPaid = input.occurrence.status === 'paid';
  const isPaidFromBank = isPaid && input.bankMatchedIds.has(input.occurrence.id);

  if (isPaidFromBank) {
    return BANK_MATCH_MARK;
  }

  return occurrenceMarkOf({
    occurrence: input.occurrence,
    kind: input.kind,
    today: input.today,
    autoGraceDays: input.settings.autoGraceDays,
    isFromBank: input.isFromBank,
  });
};

export const PeriodHistory = ({
  occurrences,
  kind,
  isFromBank,
  settings,
  bankMatchedIds,
  isPending,
  errorMessage,
  onRetry,
  onSelect,
}: PeriodHistoryProps) => {
  if (errorMessage !== null) {
    return (
      <ErrorState title="Не вдалося завантажити платежі" message={errorMessage} onRetry={onRetry} />
    );
  }

  if (isPending || occurrences === undefined) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  const today = toCivilDate(new Date(), settings.timezone);
  const arrived = occurrences.filter((occurrence) =>
    hasArrived(occurrence, formatCivilDate(today)),
  );

  if (arrived.length === 0) {
    return (
      <EmptyState
        title="Платежів ще не було"
        description="Перший з'явиться тут, щойно настане дата списання."
      />
    );
  }

  const ordered = [...arrived].sort(byPeriodStartDescending);

  return (
    <List header="Платежі">
      {ordered.map((occurrence) => (
        <OccurrenceRow
          key={occurrence.id}
          title={formatStoredDateForPeople(occurrence.dueDate)}
          value={formatOccurrenceAmount(occurrence) ?? undefined}
          valueNote={formatBaseAmount(occurrence) ?? undefined}
          mark={periodMarkOf({ occurrence, kind, isFromBank, today, settings, bankMatchedIds })}
          onClick={() => {
            onSelect(occurrence);
          }}
        />
      ))}
    </List>
  );
};
